import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Snackbar,
  AlertTitle,
} from '@mui/material';

const PaymentDashboard = () => {
  const { getAccessToken } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState('');
  const [pendingPayments, setPendingPayments] = useState([]);
  const [processedPayments, setProcessedPayments] = useState([]);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSplit, setSelectedSplit] = useState(null);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [paymentType, setPaymentType] = useState('client');
  const [securityCode, setSecurityCode] = useState('');
  const [securityDialogOpen, setSecurityDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  // API base URL - use localhost in development
  const API_BASE = process.env.NODE_ENV === 'development' 
    ? 'http://localhost:54319'
    : 'https://track.snapped.cc';


  // Fetch available months from backend
  useEffect(() => {
    const fetchMonths = async () => {
      try {
        setError(null);  // Clear any previous errors
        const token = await getAccessToken();
        const res = await fetch(`${API_BASE}/api/payments/dashboard/months?payment_type=${paymentType}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`Failed to load months: ${errorText}`);
        }
        const data = await res.json();
        if (data.months?.length > 0) {
          setMonths(data.months);
          setSelectedMonth(data.months[0]);
        } else {
          setMonths([]);
          setSelectedMonth('');
          setError(`No ${paymentType} payments found for any month`);
        }
      } catch (error) {
        console.error('Error fetching months:', error);
        setError(error.message);
        setMonths([]);
        setSelectedMonth('');
        // Don't switch back to client payments on error
      }
    };
    fetchMonths();
  }, [paymentType]);

  // Fetch payments when month or payment type changes
  useEffect(() => {
    const fetchPayments = async () => {
      if (!selectedMonth) return;
      setLoading(true);
      setError(null);  // Clear any previous errors
      try {
        // Fetch pending payments
        const token = await getAccessToken();
        const pendingRes = await fetch(`${API_BASE}/api/payments/dashboard/pending?payment_type=${paymentType}&month=${selectedMonth}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!pendingRes.ok) {
          const errorText = await pendingRes.text();
          throw new Error(`Failed to load pending payments: ${errorText}`);
        }
        const pendingData = await pendingRes.json();
        setPendingPayments(pendingData.payments || []);

        // Fetch processed payments
        const processedRes = await fetch(`${API_BASE}/api/payments/dashboard/processed?payment_type=${paymentType}&month=${selectedMonth}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!processedRes.ok) {
          const errorText = await processedRes.text();
          throw new Error(`Failed to load processed payments: ${errorText}`);
        }
        const processedData = await processedRes.json();
        setProcessedPayments(processedData.payments || []);
      } catch (error) {
        console.error('Error fetching payments:', error);
        setError(error.message);
        setPendingPayments([]);
        setProcessedPayments([]);
      }
      setLoading(false);
    };

    fetchPayments();
  }, [selectedMonth, paymentType]);

  const handleSecuritySubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      const codeToUse = securityCode; // Store the code before clearing it
      await processPayment(selectedPayment, codeToUse);
      setSecurityDialogOpen(false);
      setSecurityCode('');
    } catch (error) {
      console.error('Error in security submit:', error);
      setError(error.message);
      setSnackbarMessage(error.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setSecurityDialogOpen(true); // Keep dialog open on error
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (payment) => {
    setSelectedPayment(payment);
    setSecurityDialogOpen(true);
  };

  const processPayment = async (payment, securityCodeToUse = securityCode) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/api/payments/dashboard/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify([
          {
            statement_id: payment.statement_id,
            payee_email: payment.payee_email,
            approved: true,
            payment_type: paymentType,
            security_code: securityCodeToUse,
          },
        ]),
      });

      let responseData;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        responseData = await response.json();
        console.log('Payment approval response:', response.status, responseData);
      } else {
        responseData = await response.text();
        console.log('Payment approval response (text):', response.status, responseData);
      }

      if (!response.ok) {
        // Try to extract error message from JSON response
        let errorMessage;
        if (typeof responseData === 'object' && responseData.detail) {
          errorMessage = responseData.detail;
          
          // Parse Mercury errors more cleanly
          if (errorMessage.includes('Mercury Payment Error:')) {
            // Extract the actual error message after "Mercury Payment Error:"
            const mercuryError = errorMessage.split('Mercury Payment Error:')[1]?.trim();
            if (mercuryError) {
              errorMessage = `Payment Error: ${mercuryError}`;
            }
          }
        } else if (typeof responseData === 'string') {
          try {
            const jsonError = JSON.parse(responseData);
            errorMessage = jsonError.detail || responseData;
          } catch {
            errorMessage = responseData;
          }
        } else {
          errorMessage = 'Failed to approve payment';
        }
        throw new Error(errorMessage);
      }

      // Refresh the lists
      const pendingRes = await fetch(`${API_BASE}/api/payments/dashboard/pending?payment_type=${paymentType}&month=${selectedMonth}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!pendingRes.ok) {
        throw new Error(`Failed to refresh pending payments: ${pendingRes.status}`);
      }
      const pendingData = await pendingRes.json();
      setPendingPayments(pendingData.payments || []);

      const processedRes = await fetch(`${API_BASE}/api/payments/dashboard/processed?payment_type=${paymentType}&month=${selectedMonth}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!processedRes.ok) {
        throw new Error(`Failed to refresh processed payments: ${processedRes.status}`);
      }
      const processedData = await processedRes.json();
      setProcessedPayments(processedData.payments || []);

      // Show success notification
      setSnackbarMessage(`Payment approved successfully for ${payment.payee_name || payment.payee_email}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setError(null);

    } catch (error) {
      console.error('Error approving payment:', error);
      setError(error.message);
      setSnackbarMessage(error.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const renderPaymentSplitsTooltip = (payment) => {
    if (!payment.client_splits || payment.client_splits.length === 0) {
      return "No split details available";
    }

    return (
      <Box sx={{ maxWidth: 300 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Payment Splits:
        </Typography>
        {payment.client_splits.map((split, index) => (
          <Box key={index} sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>{split.client_email}:</strong> {formatAmount(split.split_amount)}
            </Typography>
            {split.split_percentage && (
              <Typography variant="caption" color="text.secondary">
                ({split.split_percentage}%)
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Payment Dashboard
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress />
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <ToggleButtonGroup
            value={paymentType}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                setPaymentType(newValue);
              }
            }}
            aria-label="payment type"
          >
            <ToggleButton value="client" aria-label="client payments">
              Client Payments
            </ToggleButton>
            <ToggleButton value="employee" aria-label="employee payments">
              Employee Payments
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <select 
          value={selectedMonth} 
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{ 
            fontSize: '16px', 
            padding: '12px',
            paddingRight: '36px',  // Space for the arrow
            minWidth: '200px',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            backgroundColor: '#fff',
            cursor: 'pointer',
            appearance: 'none',  // Remove default arrow
            backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007CB2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 12px top 50%',
            backgroundSize: '12px auto'
          }}
        >
          {months.map((month) => (
            <option 
              key={month} 
              value={month}
              style={{
                padding: '8px',
                fontSize: '16px'
              }}
            >
              {month}
            </option>
          ))}
        </select>
      </Box>

      {/* Pending Payments */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Pending Payments
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Payee</TableCell>
                <TableCell>Invoice #</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Start Date</TableCell>
                {paymentType === 'client' ? (
                  <TableCell>Client Splits</TableCell>
                ) : (
                  <TableCell>Hours</TableCell>
                )}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingPayments.map((payment) => (
                <TableRow 
                  key={`${payment.statement_id}-${payment.payee_email}`}
                  sx={{
                    opacity: payment.status === "processed" ? 0.5 : 1,
                    transition: "opacity 0.3s ease",
                    "&:hover": {
                      opacity: payment.status === "processed" ? 0.7 : 1
                    }
                  }}
                >
                  <TableCell>{payment.payee_name}</TableCell>
                  <TableCell>{payment.payee_email}</TableCell>
                  <TableCell>{formatAmount(payment.amount)}</TableCell>
                  <TableCell>
                    {payment.start_date ? new Date(payment.start_date).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    {paymentType === 'client' ? (
                      payment.client_splits?.map((split, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Chip
                            label={`${split.client_email}: ${formatAmount(
                              split.split_amount
                            )} (${split.split_percentage}%)`}
                            size="small"
                            onClick={() => {
                              setSelectedSplit({ ...split, payee: payment });
                              setSplitDialogOpen(true);
                            }}
                            clickable
                          />
                        </Box>
                      ))
                    ) : (
                      <Typography>
                        {payment.hours?.toFixed(2)} hours
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {paymentType === 'client' ? (
                        // Client payment actions
                        payment.status === "processed" ? (
                          <Chip 
                            label="Processed"
                            color="success"
                            size="small"
                            variant="filled"
                          />
                        ) : (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleApprove(payment)}
                          >
                            Approve
                          </Button>
                        )
                      ) : (
                        // Employee payment actions
                        <>
                          {payment.status === "processed" ? (
                            <Chip 
                              label="Processed"
                              color="success"
                              size="small"
                              variant="filled"
                            />
                          ) : (
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleApprove(payment)}
                            >
                              Approve
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setSelectedSplit({
                                payee: payment,
                                daily_work: payment.daily_work
                              });
                              setSplitDialogOpen(true);
                            }}
                          >
                            Details
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {pendingPayments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No pending payments for this month
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Processed Payments */}
      <Box>
        <Typography variant="h5" gutterBottom>
          Processed Payments
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Payee</TableCell>
                <TableCell>Invoice #</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment ID</TableCell>
                <TableCell>Processed At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedPayments.map((payment) => (
                <TableRow key={`${payment.statement_id}-${payment.payee_email}`}>
                  <TableCell>{payment.payee_name}</TableCell>
                  <TableCell>{payment.payee_email}</TableCell>
                  <TableCell>{formatAmount(payment.amount)}</TableCell>
                  <TableCell>
                    <Chip
                      label="Processed"
                      color="success"
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell>
                    {paymentType === 'employee' ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setSelectedSplit({
                              payee: payment,
                              daily_work: payment.daily_work
                            });
                            setSplitDialogOpen(true);
                          }}
                        >
                          Details
                        </Button>
                      </Box>
                    ) : (
                      <Tooltip 
                        title={renderPaymentSplitsTooltip(payment)}
                        placement="top"
                        arrow
                      >
                        <Box sx={{ 
                          cursor: 'help',
                          textDecoration: 'underline',
                          textDecorationStyle: 'dotted',
                          color: 'primary.main'
                        }}>
                          {payment.payment_id}
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {payment.processed_at
                      ? new Date(payment.processed_at).toLocaleString()
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
              {processedPayments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No processed payments for this month
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Split Details Dialog */}
      <Dialog 
        open={splitDialogOpen} 
        onClose={() => setSplitDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {paymentType === 'client' ? 'Split Details' : 'Invoice Details'}
        </DialogTitle>
        <DialogContent>
              {selectedSplit && (
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Payee"
                      secondary={`${selectedSplit.payee.payee_name} (${selectedSplit.payee.payee_email})`}
                    />
                  </ListItem>
                  <Divider />
                  {paymentType === 'client' ? (
                    // Client split details
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Client"
                          secondary={selectedSplit.client_email}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Client Total Earnings"
                          secondary={formatAmount(selectedSplit.total_amount)}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Split Percentage"
                          secondary={`${selectedSplit.split_percentage}%`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Split Amount"
                          secondary={formatAmount(selectedSplit.split_amount)}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Statement Month"
                          secondary={selectedSplit.payee.statement_month}
                        />
                      </ListItem>
                    </>
                  ) : (
                    // Employee invoice details
                    selectedSplit.daily_work?.map((day, index) => (
                      <React.Fragment key={day.date}>
                        <ListItem>
                          <ListItemText
                            primary={new Date(day.date).toLocaleDateString()}
                            secondary={`${day.hours.toFixed(2)} hours total`}
                          />
                        </ListItem>
                        {day.entries.map((entry, entryIndex) => (
                          <ListItem key={`${day.date}-${entryIndex}`} sx={{ pl: 4 }}>
                            <ListItemText
                              primary={entry.item}
                              secondary={
                                <>
                                  {entry.description}
                                  <br />
                                  {entry.hours}h {entry.minutes}m - {entry.category}
                                </>
                              }
                            />
                          </ListItem>
                        ))}
                        {index < selectedSplit.daily_work.length - 1 && <Divider />}
                      </React.Fragment>
                    ))
                  )}
                </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSplitDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Security Code Dialog */}
      <Dialog 
        open={securityDialogOpen} 
        onClose={() => {
          setSecurityDialogOpen(false);
          setSecurityCode('');
          setError(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Enter Security Code</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ mt: 1 }}>
            <input
              type="password"
              value={securityCode}
              onChange={(e) => setSecurityCode(e.target.value)}
              placeholder="Enter security code"
              style={{
                width: '90%',
                padding: '12px',
                fontSize: '16px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                marginTop: '8px'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSecuritySubmit();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSecurityDialogOpen(false);
            setSecurityCode('');
            setError(null);
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSecuritySubmit}
            variant="contained" 
            color="primary"
            disabled={!securityCode}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error/Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarSeverity === 'error' ? null : 6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: '100%', 
            maxWidth: '700px',
            fontSize: '0.95rem',
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <AlertTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
            {snackbarSeverity === 'error' ? '⚠️ Payment Failed' : '✅ Success'}
          </AlertTitle>
          <Box sx={{ 
            whiteSpace: 'pre-line', 
            fontFamily: 'system-ui',
            fontSize: '0.95rem',
            lineHeight: 1.6,
            mt: 1
          }}>
            {snackbarMessage}
          </Box>
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PaymentDashboard;
